import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Col, FormFeedback, Input, Label, Row } from 'reactstrap';
import neoLogo from "../../assets/images/Neo_Logo_A.svg";
import neoLogoC from "../../assets/images/Neo_Logo_C.svg";
import bgneo from "../../assets/images/bgneo.png";
import LoadingButton from '../../components/Common/LoadingButton';
import * as Yup from "yup";
import { useFormik } from "formik";
import { useAuth } from '../../hooks/useAuth';

const ForgetPasswordPage = () => {
  document.title = "Recuperar Senha | NeoApp - Sistema de Gestão";
  const navigate = useNavigate();
  const { resetPassword, verifyCodeAndChangePassword } = useAuth();

  // step: 1 = email, 2 = código, 3 = nova senha
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ─── Step 1: E-mail ───
  const emailForm = useFormik({
    initialValues: { email: '' },
    validationSchema: Yup.object({
      email: Yup.string().email("E-mail inválido").required("Por favor, insira seu e-mail"),
    }),
    onSubmit: async (values) => {
      setError("");
      setIsSubmitting(true);
      try {
        await resetPassword(values.email);
        setEmail(values.email);
        setStep(2);
      } catch (err) {
        setError("Erro ao enviar o código. Tente novamente.");
      } finally {
        setIsSubmitting(false);
      }
    }
  });

  // ─── Step 2: Código ───
  const [codeDigits, setCodeDigits] = useState(["", "", "", "", "", ""]);
  const codeRefs = useRef([]);

  const handleCodeChange = (index, value) => {
    if (value.length > 1) value = value.slice(-1);
    if (!/^\d*$/.test(value)) return;

    const newDigits = [...codeDigits];
    newDigits[index] = value;
    setCodeDigits(newDigits);

    // Auto-focus próximo campo
    if (value && index < 5) {
      codeRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === "Backspace" && !codeDigits[index] && index > 0) {
      codeRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    const newDigits = [...codeDigits];
    for (let i = 0; i < 6; i++) {
      newDigits[i] = pasted[i] || "";
    }
    setCodeDigits(newDigits);
    if (pasted.length === 6) {
      codeRefs.current[5]?.focus();
    }
  };

  const submitCode = () => {
    const code = codeDigits.join("");
    if (code.length !== 6) {
      setError("Digite o código completo de 6 dígitos.");
      return;
    }
    setError("");
    setStep(3);
  };

  // ─── Step 3: Nova senha ───
  const passwordForm = useFormik({
    initialValues: { password: '', confirmPassword: '' },
    validationSchema: Yup.object({
      password: Yup.string()
        .min(6, "Mínimo de 6 caracteres")
        .required("Digite a nova senha"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password')], "As senhas não coincidem")
        .required("Confirme a nova senha"),
    }),
    onSubmit: async (values) => {
      setError("");
      setIsSubmitting(true);
      try {
        const code = codeDigits.join("");
        await verifyCodeAndChangePassword(email, code, values.password);
        navigate("/login", { state: { passwordReset: true } });
      } catch (err) {
        const msg = err?.message || "";
        if (msg.includes("incorreto")) {
          setError("Código incorreto. Verifique e tente novamente.");
          setStep(2);
        } else if (msg.includes("expirado") || msg.includes("expired")) {
          setError("Código expirado. Solicite um novo.");
          setStep(1);
          setCodeDigits(["", "", "", "", "", ""]);
        } else if (msg.includes("tentativas") || msg.includes("exhausted")) {
          setError("Muitas tentativas incorretas. Solicite um novo código.");
          setStep(1);
          setCodeDigits(["", "", "", "", "", ""]);
        } else {
          setError("Erro ao redefinir a senha. Tente novamente.");
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  });

  const getStepTitle = () => {
    if (step === 1) return "Recuperar senha";
    if (step === 2) return "Verificar código";
    return "Nova senha";
  };

  const getStepSubtitle = () => {
    if (step === 1) return "Informe seu e-mail e enviaremos um código de verificação.";
    if (step === 2) return `Enviamos um código de 6 dígitos para ${email}`;
    return "Crie uma nova senha para sua conta.";
  };

  return (
    <React.Fragment>
      <div className="account-pages" style={{
        backgroundImage: `url(${bgneo})`,
        backgroundSize: 'contain',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: '#fef5ef',
        height: '100vh',
        width: '100%',
        position: 'fixed',
        top: 0,
        left: 0
      }}>
        <div className="container-fluid p-0">
          <Row className="g-0 vh-100">
            <Col lg={8} xl={9} className="d-none d-lg-block position-relative">
              <img
                src={neoLogoC}
                alt="NeoApp Logo"
                style={{
                  position: 'absolute',
                  bottom: '48px',
                  left: '48px',
                  height: '60px',
                  filter: 'brightness(0) saturate(100%) invert(18%) sepia(51%) saturate(2338%) hue-rotate(18deg) brightness(94%) contrast(92%)',
                  opacity: 0.95
                }}
              />
            </Col>

            <Col lg={4} xl={3} className="d-flex align-items-center neo-auth-col">
              <div className="p-4 p-sm-5 w-100">
                <div className="mb-5 text-center">
                  <Link to="/" className="d-block auth-logo">
                    <img src={neoLogo} alt="NeoApp" height="38" className="auth-logo-dark mx-auto" />
                  </Link>
                </div>

                {/* Step indicator */}
                <div className="d-flex justify-content-center mb-4 gap-2">
                  {[1, 2, 3].map(s => (
                    <div key={s} style={{
                      width: step >= s ? '32px' : '8px',
                      height: '8px',
                      borderRadius: '4px',
                      backgroundColor: step >= s ? 'var(--bs-primary)' : '#dee2e6',
                      transition: 'all 0.3s ease'
                    }} />
                  ))}
                </div>

                <div className="mb-4">
                  <h4 className="neo-auth-title">{getStepTitle()}</h4>
                  <p className="neo-auth-subtitle" style={{ fontSize: '0.9rem' }}>{getStepSubtitle()}</p>
                </div>

                {error && (
                  <Alert className="neo-auth-alert" fade={false}>
                    <i className="mdi mdi-alert-circle-outline me-2"></i>
                    {error}
                  </Alert>
                )}

                {/* ─── Step 1: E-mail ─── */}
                {step === 1 && (
                  <form onSubmit={(e) => { e.preventDefault(); emailForm.handleSubmit(); }}>
                    <div className="mb-3">
                      <Label className="neo-auth-label">E-mail</Label>
                      <Input
                        name="email"
                        className="neo-auth-input"
                        placeholder="Digite seu e-mail"
                        type="email"
                        onChange={emailForm.handleChange}
                        onBlur={emailForm.handleBlur}
                        value={emailForm.values.email}
                        invalid={emailForm.touched.email && !!emailForm.errors.email}
                      />
                      {emailForm.touched.email && emailForm.errors.email && (
                        <FormFeedback>{emailForm.errors.email}</FormFeedback>
                      )}
                    </div>
                    <div className="mt-4 d-grid">
                      <LoadingButton className="neo-auth-btn-primary" type="submit" loading={isSubmitting}>
                        Enviar código
                      </LoadingButton>
                    </div>
                  </form>
                )}

                {/* ─── Step 2: Código ─── */}
                {step === 2 && (
                  <div>
                    <div className="d-flex justify-content-center gap-2 mb-4" onPaste={handleCodePaste}>
                      {codeDigits.map((digit, i) => (
                        <input
                          key={i}
                          ref={el => codeRefs.current[i] = el}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={digit}
                          onChange={e => handleCodeChange(i, e.target.value)}
                          onKeyDown={e => handleCodeKeyDown(i, e)}
                          autoFocus={i === 0}
                          style={{
                            width: '46px',
                            height: '54px',
                            textAlign: 'center',
                            fontSize: '1.5rem',
                            fontWeight: '700',
                            border: '2px solid #dee2e6',
                            borderRadius: '8px',
                            outline: 'none',
                            transition: 'border-color 0.2s',
                          }}
                          onFocus={e => e.target.style.borderColor = 'var(--bs-primary)'}
                          onBlur={e => e.target.style.borderColor = '#dee2e6'}
                        />
                      ))}
                    </div>
                    <div className="d-grid">
                      <LoadingButton className="neo-auth-btn-primary" onClick={submitCode} loading={isSubmitting}>
                        Verificar código
                      </LoadingButton>
                    </div>
                    <div className="text-center mt-3">
                      <button
                        className="btn btn-link neo-auth-link p-0"
                        style={{ fontSize: '0.85rem' }}
                        onClick={() => { setStep(1); setCodeDigits(["","","","","",""]); setError(""); }}
                      >
                        Não recebeu? Enviar novamente
                      </button>
                    </div>
                  </div>
                )}

                {/* ─── Step 3: Nova senha ─── */}
                {step === 3 && (
                  <form onSubmit={(e) => { e.preventDefault(); passwordForm.handleSubmit(); }}>
                    <div className="mb-3">
                      <Label className="neo-auth-label">Nova senha</Label>
                      <Input
                        name="password"
                        type="password"
                        className="neo-auth-input"
                        placeholder="Mínimo 6 caracteres"
                        onChange={passwordForm.handleChange}
                        onBlur={passwordForm.handleBlur}
                        value={passwordForm.values.password}
                        invalid={passwordForm.touched.password && !!passwordForm.errors.password}
                      />
                      {passwordForm.touched.password && passwordForm.errors.password && (
                        <FormFeedback>{passwordForm.errors.password}</FormFeedback>
                      )}
                    </div>
                    <div className="mb-3">
                      <Label className="neo-auth-label">Confirmar senha</Label>
                      <Input
                        name="confirmPassword"
                        type="password"
                        className="neo-auth-input"
                        placeholder="Repita a nova senha"
                        onChange={passwordForm.handleChange}
                        onBlur={passwordForm.handleBlur}
                        value={passwordForm.values.confirmPassword}
                        invalid={passwordForm.touched.confirmPassword && !!passwordForm.errors.confirmPassword}
                      />
                      {passwordForm.touched.confirmPassword && passwordForm.errors.confirmPassword && (
                        <FormFeedback>{passwordForm.errors.confirmPassword}</FormFeedback>
                      )}
                    </div>
                    <div className="mt-4 d-grid">
                      <LoadingButton className="neo-auth-btn-primary" type="submit" loading={isSubmitting}>
                        Redefinir senha
                      </LoadingButton>
                    </div>
                  </form>
                )}

                <div className="mt-4 text-center">
                  <Link to="/login" className="neo-auth-link">
                    <i className="mdi mdi-arrow-left me-1"></i>
                    Voltar para o login
                  </Link>
                </div>

                <div className="mt-5 text-center text-muted">
                  <p className="mb-0">© {new Date().getFullYear()} NeoApp</p>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
};

export default ForgetPasswordPage;
